import { STORAGE_RECOVERY_MESSAGES } from "@econ-v1/ports";
import { BrowserWriterLease, OpfsDatabaseStorage, } from "./opfs-database.js";
import { CLIENT_DATABASE_FILENAME, OpfsSahPoolRuntimeOwner, } from "./sqlite-runtime.js";
import { DEFAULT_REPOSITORY_OPERATION_TIMEOUTS_MS, executeRepositoryWorkerRequest, isRepositoryWorkerOperation, parseRepositoryWorkerRequest, projectRepositoryWorkerResponse, } from "./storage-repository-protocol.js";
const DIAGNOSTIC_ITERATIONS = 310_000;
const DEFAULT_PREPARATION_TIMEOUT_MS = 15_000;
const STORAGE_WORKER_FAILURE_ERROR = "Storage worker operation failed";
const DEFAULT_OPERATION_TIMEOUTS_MS = {
    ...DEFAULT_REPOSITORY_OPERATION_TIMEOUTS_MS,
    close: 30_000,
    "export-diagnostic": 300_000,
    initialize: 180_000,
    "refresh-identity-metadata": 60_000,
    "recovery-state": 15_000,
    reset: 180_000,
    retry: 180_000,
};
export class DedicatedWorkerStoragePort {
    repositories = {
        agents: {
            apply: async (entity) => await this.#repositoryRequest("repository.revisioned.agent.apply", () => ({ entity })),
            command: async (input) => await this.#repositoryRequest("repository.revisioned.agent.command", () => ({ input })),
            list: async () => await this.#repositoryRequest("repository.revisioned.agent.list", () => undefined),
            read: async (id) => (await this.#repositoryRequest("repository.revisioned.agent.read", () => ({ id }))) ?? undefined,
        },
        conversations: {
            acknowledgeMessage: async (input) => await this.#repositoryRequest("repository.conversations.acknowledge-message", () => ({ input })),
            appendPendingMessage: (input) => ({
                commit: async () => await this.#repositoryRequest("repository.conversations.append-pending-message", () => ({ input })),
            }),
            applyDelta: (delta) => ({
                commit: async () => await this.#repositoryRequest("repository.conversations.apply-delta", () => ({ delta })),
            }),
            failMessage: async (input) => await this.#repositoryRequest("repository.conversations.fail-message", () => ({ input })),
            listConversations: async (params = {}) => await this.#repositoryRequest("repository.conversations.list-conversations", () => ({ params })),
            listMessages: async (conversationId, params = {}) => await this.#repositoryRequest("repository.conversations.list-messages", () => ({ conversationId, params })),
        },
        events: {
            listUndelivered: async () => await this.#repositoryRequest("repository.events.list-undelivered", () => undefined),
            markDelivered: async (sequence, deliveredAt) => {
                await this.#repositoryRequest("repository.events.mark-delivered", () => ({ deliveredAt, sequence }));
            },
            markDeliveryAttempt: async (sequence) => {
                await this.#repositoryRequest("repository.events.mark-delivery-attempt", () => ({ sequence }));
            },
        },
        outbox: {
            create: async (input) => await this.#repositoryRequest("repository.outbox.create", () => ({ input })),
            claimHead: async (input) => (await this.#repositoryRequest("repository.outbox.claim-head", () => input)) ?? undefined,
            delete: async (id) => await this.#repositoryRequest("repository.outbox.delete", () => ({ id })),
            list: async (filter = {}) => await this.#repositoryRequest("repository.outbox.list", () => filter),
            listStatus: async (filter = {}) => await this.#repositoryRequest("repository.outbox.list-status", () => filter),
            read: async (id) => (await this.#repositoryRequest("repository.outbox.read", () => ({ id }))) ?? undefined,
            settle: async (input) => await this.#repositoryRequest("repository.outbox.settle", () => input),
            updateState: async (id, state) => await this.#repositoryRequest("repository.outbox.update-state", () => ({ id, state })),
        },
        settings: {
            delete: async (key) => await this.#repositoryRequest("repository.settings.delete", () => ({ key })),
            list: async () => await this.#repositoryRequest("repository.settings.list", () => undefined),
            read: async (key) => (await this.#repositoryRequest("repository.settings.read", () => ({ key }))) ?? undefined,
            set: async (input) => await this.#repositoryRequest("repository.settings.set", () => ({ input })),
        },
        notifications: {
            applyDelta: (delta) => ({
                commit: async () => await this.#repositoryRequest("repository.notifications.apply-delta", () => ({ delta })),
            }),
            listNotifications: async (params = {}) => await this.#repositoryRequest("repository.notifications.list-notifications", () => ({ params })),
            markRead: (input) => ({
                commit: async () => await this.#repositoryRequest("repository.notifications.mark-read", () => ({ input })),
            }),
        },
        graphs: {
            apply: async (entity) => await this.#repositoryRequest("repository.revisioned.graph.apply", () => ({ entity })),
            command: async (input) => await this.#repositoryRequest("repository.revisioned.graph.command", () => ({ input })),
            list: async () => await this.#repositoryRequest("repository.revisioned.graph.list", () => undefined),
            read: async (id) => (await this.#repositoryRequest("repository.revisioned.graph.read", () => ({ id }))) ?? undefined,
        },
        revisionedSettings: {
            apply: async (entity) => await this.#repositoryRequest("repository.revisioned.settings.apply", () => ({ entity })),
            command: async (input) => await this.#repositoryRequest("repository.revisioned.settings.command", () => ({ input })),
            list: async () => await this.#repositoryRequest("repository.revisioned.settings.list", () => undefined),
            read: async (id) => (await this.#repositoryRequest("repository.revisioned.settings.read", () => ({ id }))) ?? undefined,
        },
        wallet: {
            readCommandResult: async (idempotencyKey) => (await this.#repositoryRequest("repository.wallet.read-command-result", () => ({ idempotencyKey }))) ?? undefined,
            readSnapshot: async () => (await this.#repositoryRequest("repository.wallet.read-snapshot", () => undefined)) ?? undefined,
            writeCommandResult: async (idempotencyKey, result) => {
                await this.#repositoryRequest("repository.wallet.write-command-result", () => ({ idempotencyKey, result }));
            },
            writeSnapshot: async (snapshot) => {
                await this.#repositoryRequest("repository.wallet.write-snapshot", () => ({ snapshot }));
            },
        },
        syncLedger: {
            delete: async (domain) => await this.#repositoryRequest("repository.sync-ledger.delete", () => ({ domain })),
            list: async () => await this.#repositoryRequest("repository.sync-ledger.list", () => undefined),
            read: async (domain) => (await this.#repositoryRequest("repository.sync-ledger.read", () => ({ domain }))) ?? undefined,
            setCursor: async (input) => await this.#repositoryRequest("repository.sync-ledger.set-cursor", () => ({ input })),
        },
    };
    stages;
    #crypto;
    #identity;
    #recoveryStateListeners = new Set();
    #pending = new Map();
    #operationTimeoutsMs;
    #preparationTimeoutMs;
    #workerFactory;
    #outboundQueue = Promise.resolve();
    #closed = false;
    #closePromise;
    #nextId = 1;
    #state = { schemaVersion: 0, status: "starting" };
    #worker;
    constructor(options) {
        this.#crypto = options.crypto ?? globalThis.crypto;
        this.#identity = options.identity;
        this.#preparationTimeoutMs = positiveTimeout(options.requestTimeouts?.preparationMs ?? DEFAULT_PREPARATION_TIMEOUT_MS, "preparation");
        this.#operationTimeoutsMs = Object.fromEntries(Object.entries(DEFAULT_OPERATION_TIMEOUTS_MS).map(([operation, defaultTimeout]) => [
            operation,
            positiveTimeout(options.requestTimeouts?.operations?.[operation] ?? defaultTimeout, operation),
        ]));
        this.stages = options.stages;
        this.#workerFactory = options.workerFactory;
        this.#worker = options.worker;
        if (this.#worker !== undefined)
            this.#attachWorker(this.#worker);
    }
    async initialize() {
        return await this.#requestRecoveryState("initialize", async () => await collectLegacyIdentityMetadata(this.#identity));
    }
    async recoveryState() {
        if (this.#state.status === "recovery-required" && this.#pending.size === 0)
            return this.#state;
        return await this.#requestRecoveryState("recovery-state", () => undefined);
    }
    subscribeRecoveryState(listener) {
        this.#recoveryStateListeners.add(listener);
        listener(this.#state);
        return () => { this.#recoveryStateListeners.delete(listener); };
    }
    async refreshIdentityMetadata() {
        try {
            const state = await this.#request("refresh-identity-metadata", async () => await collectLegacyIdentityMetadata(this.#identity));
            this.#setState(state);
        }
        catch (error) {
            if (!(error instanceof StorageWorkerUnavailableError))
                throw error;
        }
    }
    async retry() {
        return await this.#requestRecoveryState("retry", async () => await collectLegacyIdentityMetadata(this.#identity));
    }
    async exportEncryptedDiagnostic(options) {
        if (options.passphrase.length < 12) {
            throw new TypeError("Diagnostic passphrase must be at least 12 characters");
        }
        try {
            return await this.#request("export-diagnostic", async () => await deriveDiagnosticEncryption(options.passphrase, this.#crypto));
        }
        catch (error) {
            if (error instanceof StorageWorkerUnavailableError)
                throw new Error("Storage worker is unavailable");
            if (this.#state.status !== "recovery-required")
                this.#setDiagnosticFailureState();
            throw new Error("Storage diagnostic export failed");
        }
    }
    async reset(options) {
        return await this.#requestRecoveryState("reset", () => options);
    }
    close() {
        this.#closePromise ??= this.#close();
        return this.#closePromise;
    }
    async #close() {
        if (this.#closed)
            return;
        try {
            if (this.#worker !== undefined)
                await this.#request("close", () => undefined);
        }
        catch {
            // Close is terminal. Typed Worker failure state is retained locally while
            // teardown continues so application shutdown cannot be stranded.
        }
        finally {
            this.#closed = true;
            this.#worker?.terminate();
            this.#worker = undefined;
            for (const pending of this.#pending.values()) {
                clearDeadline(pending);
                pending.cancelPayload();
                pending.reject(new Error("Storage worker closed"));
            }
            this.#pending.clear();
        }
    }
    async #requestRecoveryState(operation, payload) {
        try {
            const state = await this.#request(operation, payload);
            this.#setState(state);
            return state;
        }
        catch (error) {
            if (error instanceof StorageWorkerUnavailableError)
                return this.#state;
            throw error;
        }
    }
    async #repositoryRequest(operation, payload) {
        if (this.#state.status !== "ready")
            throw new Error("Storage is not ready");
        return await this.#request(operation, payload);
    }
    async #request(operation, payload) {
        if (this.#closed)
            throw new Error("Storage worker is closed");
        const worker = this.#ensureWorker();
        const id = this.#nextId;
        this.#nextId += 1;
        let cancelPayload = () => undefined;
        const payloadCancelled = new Promise((resolve) => { cancelPayload = () => resolve(true); });
        const response = new Promise((resolve, reject) => {
            this.#pending.set(id, {
                cancelPayload,
                deadline: undefined,
                operation,
                reject,
                resolve: (value) => resolve(value),
            });
        });
        const task = this.#outboundQueue.then(async () => {
            if (!this.#pending.has(id) || this.#worker !== worker)
                return;
            this.#startDeadline(id, worker, this.#preparationTimeoutMs, "worker-preparation-timeout");
            let prepared;
            try {
                prepared = await Promise.race([
                    Promise.resolve().then(payload).then((value) => ({ cancelled: false, value })),
                    payloadCancelled.then(() => ({ cancelled: true })),
                ]);
            }
            catch (error) {
                const pending = this.#pending.get(id);
                if (pending === undefined)
                    return;
                this.#pending.delete(id);
                clearDeadline(pending);
                pending.cancelPayload();
                pending.reject(error instanceof Error ? error : new Error("Storage request preparation failed"));
                return;
            }
            if (prepared.cancelled || !this.#pending.has(id) || this.#worker !== worker)
                return;
            this.#startDeadline(id, worker, this.#operationTimeoutsMs[operation], "worker-operation-timeout");
            try {
                worker.postMessage(storageWorkerRequest(id, operation, prepared.value));
            }
            catch {
                this.#failWorker(worker);
            }
            await response.then(() => undefined, () => undefined);
        });
        this.#outboundQueue = task.then(() => undefined, () => undefined);
        return await response;
    }
    #startDeadline(id, worker, timeoutMs, code) {
        const pending = this.#pending.get(id);
        if (pending === undefined)
            return;
        clearDeadline(pending);
        pending.deadline = globalThis.setTimeout(() => this.#failWorker(worker, code), timeoutMs);
    }
    #ensureWorker() {
        if (this.#worker !== undefined)
            return this.#worker;
        try {
            const worker = this.#workerFactory?.();
            if (worker === undefined)
                throw new Error("Storage worker factory is unavailable");
            this.#worker = worker;
            this.#attachWorker(worker);
            return worker;
        }
        catch {
            this.#setUnavailableState();
            throw new StorageWorkerUnavailableError();
        }
    }
    #attachWorker(worker) {
        worker.addEventListener("message", (event) => {
            let response;
            try {
                response = parseStorageWorkerResponse(event.data);
            }
            catch {
                response = undefined;
            }
            if (response === undefined) {
                this.#failWorker(worker);
                return;
            }
            this.#handleResponse(worker, response);
        });
        worker.addEventListener("error", () => this.#failWorker(worker));
        worker.addEventListener("messageerror", () => this.#failWorker(worker));
    }
    #failWorker(worker, code = "opfs-unavailable") {
        if (this.#worker !== worker)
            return;
        worker.terminate();
        this.#worker = undefined;
        this.#setWorkerFailureState(code);
        const error = new StorageWorkerUnavailableError();
        for (const pending of this.#pending.values()) {
            clearDeadline(pending);
            pending.cancelPayload();
            pending.reject(error);
        }
        this.#pending.clear();
    }
    #setUnavailableState() {
        this.#setWorkerFailureState("opfs-unavailable");
    }
    #setWorkerFailureState(code) {
        this.#setState({
            actions: { exportDiagnostic: false, reset: true, retry: true },
            code,
            message: STORAGE_RECOVERY_MESSAGES[code],
            schemaVersion: this.#state.schemaVersion,
            ...(this.#state.storageGeneration === undefined
                ? {}
                : { storageGeneration: this.#state.storageGeneration }),
            status: "recovery-required",
        });
    }
    #setDiagnosticFailureState() {
        this.#setState({
            actions: { exportDiagnostic: true, reset: true, retry: true },
            code: "diagnostic-failed",
            message: STORAGE_RECOVERY_MESSAGES["diagnostic-failed"],
            schemaVersion: this.#state.schemaVersion,
            ...(this.#state.storageGeneration === undefined
                ? {}
                : { storageGeneration: this.#state.storageGeneration }),
            status: "recovery-required",
        });
    }
    #setState(state) {
        const nextState = state.storageGeneration === undefined && this.#state.storageGeneration !== undefined
            ? { ...state, storageGeneration: this.#state.storageGeneration }
            : state;
        if (sameRecoveryState(this.#state, nextState))
            return;
        this.#state = nextState;
        for (const listener of this.#recoveryStateListeners) {
            try {
                listener(nextState);
            }
            catch {
                // A consumer cannot interrupt storage ownership or other readiness subscribers.
            }
        }
    }
    #handleResponse(worker, value) {
        const pending = this.#pending.get(value.id);
        if (pending === undefined) {
            this.#failWorker(worker);
            return;
        }
        if (value.ok) {
            const projected = projectSuccessfulResponseValue(pending.operation, value.value);
            if (projected === undefined) {
                this.#failWorker(worker);
                return;
            }
            this.#pending.delete(value.id);
            clearDeadline(pending);
            pending.cancelPayload();
            pending.resolve(projected.value);
            return;
        }
        this.#pending.delete(value.id);
        clearDeadline(pending);
        pending.cancelPayload();
        this.#setState(value.state);
        pending.reject(new Error(STORAGE_WORKER_FAILURE_ERROR));
    }
}
function clearDeadline(pending) {
    if (pending.deadline === undefined)
        return;
    globalThis.clearTimeout(pending.deadline);
    pending.deadline = undefined;
}
function positiveTimeout(value, phase) {
    if (!Number.isSafeInteger(value) || value <= 0) {
        throw new TypeError(`Storage Worker ${phase} timeout must be a positive integer`);
    }
    return value;
}
function storageWorkerRequest(id, operation, payload) {
    return {
        id,
        operation,
        ...(payload === undefined ? {} : { payload }),
    };
}
async function deriveDiagnosticEncryption(passphrase, crypto) {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const passphraseBytes = new TextEncoder().encode(passphrase);
    try {
        const material = await crypto.subtle.importKey("raw", passphraseBytes, "PBKDF2", false, ["deriveKey"]);
        const key = await crypto.subtle.deriveKey({ hash: "SHA-256", iterations: DIAGNOSTIC_ITERATIONS, name: "PBKDF2", salt }, material, { length: 256, name: "AES-GCM" }, false, ["encrypt"]);
        return {
            kdf: {
                hash: "SHA-256",
                iterations: DIAGNOSTIC_ITERATIONS,
                name: "PBKDF2",
                saltBase64: base64(salt),
            },
            key,
        };
    }
    finally {
        passphraseBytes.fill(0);
    }
}
function base64(bytes) {
    let binary = "";
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary);
}
class StorageWorkerUnavailableError extends Error {
    constructor() {
        super("Storage worker is unavailable");
        this.name = "StorageWorkerUnavailableError";
    }
}
export function createDedicatedWorkerStoragePort(options) {
    return new DedicatedWorkerStoragePort({
        identity: options.identity,
        stages: options.stages,
        workerFactory: () => new Worker(options.workerUrl, {
            name: "client-node-sqlite-storage",
            type: "module",
        }),
    });
}
export function hostDedicatedStorageWorker(scope, options = {}) {
    let identityMetadata;
    const runtimeOwner = new OpfsSahPoolRuntimeOwner({ filename: CLIENT_DATABASE_FILENAME });
    const storage = options.storage ?? new OpfsDatabaseStorage({
        deleteDatabase: async () => await runtimeOwner.wipe(),
        filename: CLIENT_DATABASE_FILENAME,
        identityMetadata: async () => identityMetadata,
        lease: new BrowserWriterLease(),
        runtimeFactory: async () => await runtimeOwner.open(),
    });
    let operationQueue = Promise.resolve();
    scope.onmessage = (event) => {
        const request = parseStorageWorkerRequest(event.data);
        if (request === undefined)
            return;
        const task = operationQueue.then(async () => await handleWorkerRequest(storage, request, (response) => scope.postMessage(response), (snapshot) => { identityMetadata = snapshot; }));
        operationQueue = task.then(() => undefined, () => undefined);
    };
}
async function collectLegacyIdentityMetadata(identity) {
    const [publicIdentity, delegation, connectionMetadata] = await Promise.all([
        readIdentitySource(async () => await identity.publicIdentity(), (value) => ({
            custodyMode: value.custodyMode,
            did: value.did,
            nodeId: value.nodeId,
            publicKeyMultibase: value.publicKeyMultibase,
        })),
        readIdentitySource(async () => await identity.loadDelegation(), (value) => ({
            expiresAt: value.expiresAt,
            issuedAt: value.issuedAt,
            revocationId: value.revocationId,
            scopes: [...value.scopes],
        })),
        readIdentitySource(async () => await identity.loadConnectionMetadata(), (value) => ({
            directAddresses: [...value.directAddresses],
            ...(value.homeNodeId === undefined ? {} : { homeNodeId: value.homeNodeId }),
            ...(value.lastConnectedAt === undefined ? {} : { lastConnectedAt: value.lastConnectedAt }),
            ...(value.relayUrl === undefined ? {} : { relayUrl: value.relayUrl }),
            ...(value.wanOrigin === undefined ? {} : { wanOrigin: value.wanOrigin }),
        })),
    ]);
    return { connectionMetadata, delegation, publicIdentity };
}
async function readIdentitySource(read, project) {
    try {
        const value = await read();
        return value === undefined
            ? { status: "absent" }
            : { status: "present", value: project(value) };
    }
    catch {
        return { status: "failed" };
    }
}
async function handleWorkerRequest(storage, value, respond, setIdentityMetadata) {
    try {
        let result;
        if (isRepositoryWorkerOperation(value.operation)) {
            const state = await storage.recoveryState();
            if (state.status !== "ready")
                throw new Error("Storage is not ready");
            result = await executeRepositoryWorkerRequest(storage.repositories, value);
            respond({ id: value.id, ok: true, value: result });
            return;
        }
        switch (value.operation) {
            case "initialize":
                setIdentityMetadata(value.payload);
                result = await storage.initialize();
                break;
            case "recovery-state":
                result = await storage.recoveryState();
                break;
            case "refresh-identity-metadata": {
                const snapshot = value.payload;
                setIdentityMetadata(snapshot);
                await storage.refreshIdentityMetadata(snapshot);
                result = await storage.recoveryState();
                break;
            }
            case "retry":
                setIdentityMetadata(value.payload);
                result = await storage.retry();
                break;
            case "export-diagnostic":
                result = await storage.exportEncryptedDiagnostic(value.payload);
                break;
            case "reset":
                result = await storage.reset(value.payload);
                break;
            case "close":
                await storage.close();
                result = null;
                break;
        }
        respond({ id: value.id, ok: true, value: result });
    }
    catch {
        respond({
            error: STORAGE_WORKER_FAILURE_ERROR,
            id: value.id,
            ok: false,
            state: await storage.recoveryState(),
        });
    }
}
function parseStorageWorkerRequest(value) {
    if (!isRecord(value) || !Number.isSafeInteger(value.id) || value.id < 1)
        return undefined;
    const repositoryRequest = parseRepositoryWorkerRequest(value);
    if (repositoryRequest !== undefined)
        return repositoryRequest;
    const id = value.id;
    switch (value.operation) {
        case "close":
        case "recovery-state":
            return hasExactOwnKeys(value, ["id", "operation"])
                ? { id, operation: value.operation }
                : undefined;
        case "initialize":
        case "refresh-identity-metadata":
        case "retry": {
            if (!hasExactOwnKeys(value, ["id", "operation", "payload"]))
                return undefined;
            const payload = projectIdentityMetadataSnapshot(value.payload);
            return payload === undefined ? undefined : { id, operation: value.operation, payload };
        }
        case "export-diagnostic": {
            if (!hasExactOwnKeys(value, ["id", "operation", "payload"]))
                return undefined;
            const payload = projectDiagnosticEncryption(value.payload);
            return payload === undefined ? undefined : { id, operation: "export-diagnostic", payload };
        }
        case "reset": {
            if (!hasExactOwnKeys(value, ["id", "operation", "payload"]) ||
                !isRecord(value.payload) ||
                !hasExactOwnKeys(value.payload, ["confirmation"]) ||
                typeof value.payload.confirmation !== "string")
                return undefined;
            return { id, operation: "reset", payload: { confirmation: value.payload.confirmation } };
        }
        default:
            return undefined;
    }
}
function parseStorageWorkerResponse(value) {
    if (!isRecord(value) || !Number.isSafeInteger(value.id))
        return undefined;
    if (value.ok === true) {
        if (!hasExactOwnKeys(value, ["id", "ok", "value"]))
            return undefined;
        return { id: value.id, ok: true, value: value.value };
    }
    if (value.ok !== false ||
        !hasExactOwnKeys(value, ["error", "id", "ok", "state"]) ||
        value.error !== STORAGE_WORKER_FAILURE_ERROR)
        return undefined;
    const state = projectStorageRecoveryState(value.state);
    return state === undefined
        ? undefined
        : { error: STORAGE_WORKER_FAILURE_ERROR, id: value.id, ok: false, state };
}
function projectSuccessfulResponseValue(operation, value) {
    if (isRepositoryWorkerOperation(operation)) {
        const projected = projectRepositoryWorkerResponse(operation, value);
        return projected === undefined ? undefined : { value: projected };
    }
    switch (operation) {
        case "close":
            return value === null ? { value: null } : undefined;
        case "export-diagnostic": {
            const diagnostic = projectEncryptedStorageDiagnostic(value);
            return diagnostic === undefined ? undefined : { value: diagnostic };
        }
        case "initialize":
        case "refresh-identity-metadata":
        case "recovery-state":
        case "reset":
        case "retry": {
            const state = projectStorageRecoveryState(value);
            return state === undefined ? undefined : { value: state };
        }
    }
}
function sameRecoveryState(left, right) {
    if (left.status !== right.status ||
        left.schemaVersion !== right.schemaVersion ||
        left.storageGeneration !== right.storageGeneration)
        return false;
    if (left.status !== "recovery-required" || right.status !== "recovery-required")
        return true;
    return left.code === right.code &&
        left.message === right.message &&
        left.actions.exportDiagnostic === right.actions.exportDiagnostic;
}
function projectStorageRecoveryState(value) {
    if (!isRecord(value) ||
        typeof value.schemaVersion !== "number" ||
        !Number.isSafeInteger(value.schemaVersion) ||
        value.schemaVersion < 0)
        return undefined;
    if (value.status === "ready" || value.status === "starting") {
        const storageGeneration = projectStorageGeneration(value);
        if (!hasOptionalGenerationKeys(value, ["schemaVersion", "status"]) || storageGeneration === null) {
            return undefined;
        }
        return {
            schemaVersion: value.schemaVersion,
            ...(storageGeneration === undefined ? {} : { storageGeneration }),
            status: value.status,
        };
    }
    const storageGeneration = projectStorageGeneration(value);
    if (value.status !== "recovery-required" ||
        !isStorageRecoveryCode(value.code) ||
        value.message !== STORAGE_RECOVERY_MESSAGES[value.code] ||
        !hasOptionalGenerationKeys(value, ["actions", "code", "message", "schemaVersion", "status"]) ||
        storageGeneration === null ||
        !isRecord(value.actions) ||
        !hasExactOwnKeys(value.actions, ["exportDiagnostic", "reset", "retry"]) ||
        typeof value.actions.exportDiagnostic !== "boolean" ||
        value.actions.reset !== true ||
        value.actions.retry !== true) {
        return undefined;
    }
    return {
        actions: {
            exportDiagnostic: value.actions.exportDiagnostic,
            reset: true,
            retry: true,
        },
        code: value.code,
        message: STORAGE_RECOVERY_MESSAGES[value.code],
        schemaVersion: value.schemaVersion,
        ...(storageGeneration === undefined ? {} : { storageGeneration }),
        status: "recovery-required",
    };
}
function projectStorageGeneration(value) {
    if (!Object.hasOwn(value, "storageGeneration"))
        return undefined;
    return typeof value.storageGeneration === "number" &&
        Number.isSafeInteger(value.storageGeneration) &&
        value.storageGeneration > 0
        ? value.storageGeneration
        : null;
}
function hasOptionalGenerationKeys(value, required) {
    const expected = Object.hasOwn(value, "storageGeneration")
        ? [...required, "storageGeneration"]
        : required;
    return hasExactOwnKeys(value, expected);
}
function isStorageRecoveryCode(value) {
    return value === "checksum-drift" ||
        value === "corrupt" ||
        value === "diagnostic-failed" ||
        value === "identity-source-unavailable" ||
        value === "migration-failed" ||
        value === "opfs-unavailable" ||
        value === "quota-exceeded" ||
        value === "worker-operation-timeout" ||
        value === "worker-preparation-timeout" ||
        value === "writer-conflict";
}
function projectEncryptedStorageDiagnostic(value) {
    if (!isRecord(value) || !hasExactOwnKeys(value, [
        "algorithm",
        "ciphertextBase64",
        "createdAt",
        "format",
        "ivBase64",
        "kdf",
    ]))
        return undefined;
    const kdf = projectDiagnosticKdf(value.kdf);
    if (kdf === undefined ||
        value.algorithm !== "AES-GCM" ||
        typeof value.ciphertextBase64 !== "string" ||
        typeof value.createdAt !== "string" ||
        value.format !== "node-client-sqlite-diagnostic-v1" ||
        typeof value.ivBase64 !== "string")
        return undefined;
    return {
        algorithm: "AES-GCM",
        ciphertextBase64: value.ciphertextBase64,
        createdAt: value.createdAt,
        format: "node-client-sqlite-diagnostic-v1",
        ivBase64: value.ivBase64,
        kdf,
    };
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function hasExactOwnKeys(value, expected) {
    const keys = Reflect.ownKeys(value);
    return keys.length === expected.length && expected.every((key) => Object.hasOwn(value, key));
}
function projectIdentityMetadataSnapshot(value) {
    if (!isRecord(value) || !hasExactOwnKeys(value, ["connectionMetadata", "delegation", "publicIdentity"])) {
        return undefined;
    }
    const connectionMetadata = projectIdentityRead(value.connectionMetadata, projectConnectionMetadata);
    const delegation = projectIdentityRead(value.delegation, projectDelegationMetadata);
    const publicIdentity = projectIdentityRead(value.publicIdentity, projectPublicIdentity);
    if (connectionMetadata === undefined || delegation === undefined || publicIdentity === undefined)
        return undefined;
    return { connectionMetadata, delegation, publicIdentity };
}
function projectIdentityRead(value, project) {
    if (!isRecord(value) || typeof value.status !== "string")
        return undefined;
    if (value.status === "absent" || value.status === "failed") {
        return hasExactOwnKeys(value, ["status"]) ? { status: value.status } : undefined;
    }
    if (value.status !== "present" || !hasExactOwnKeys(value, ["status", "value"]))
        return undefined;
    const projected = project(value.value);
    return projected === undefined ? undefined : { status: "present", value: projected };
}
function projectPublicIdentity(value) {
    if (!isRecord(value) ||
        !hasExactOwnKeys(value, ["custodyMode", "did", "nodeId", "publicKeyMultibase"]) ||
        (value.custodyMode !== "non-extractable" && value.custodyMode !== "wrapped") ||
        typeof value.did !== "string" ||
        typeof value.nodeId !== "string" ||
        typeof value.publicKeyMultibase !== "string")
        return undefined;
    return {
        custodyMode: value.custodyMode,
        did: value.did,
        nodeId: value.nodeId,
        publicKeyMultibase: value.publicKeyMultibase,
    };
}
function projectDelegationMetadata(value) {
    if (!isRecord(value) ||
        !hasExactOwnKeys(value, ["expiresAt", "issuedAt", "revocationId", "scopes"]) ||
        typeof value.expiresAt !== "string" ||
        typeof value.issuedAt !== "string" ||
        typeof value.revocationId !== "string" ||
        !Array.isArray(value.scopes) ||
        !value.scopes.every((scope) => typeof scope === "string"))
        return undefined;
    return {
        expiresAt: value.expiresAt,
        issuedAt: value.issuedAt,
        revocationId: value.revocationId,
        scopes: [...value.scopes],
    };
}
function projectConnectionMetadata(value) {
    if (!isRecord(value) || !Array.isArray(value.directAddresses) ||
        !value.directAddresses.every((address) => typeof address === "string"))
        return undefined;
    const expected = [
        "directAddresses",
        ...(Object.hasOwn(value, "homeNodeId") ? ["homeNodeId"] : []),
        ...(Object.hasOwn(value, "lastConnectedAt") ? ["lastConnectedAt"] : []),
        ...(Object.hasOwn(value, "relayUrl") ? ["relayUrl"] : []),
        ...(Object.hasOwn(value, "wanOrigin") ? ["wanOrigin"] : []),
    ];
    if (!hasExactOwnKeys(value, expected) ||
        (value.homeNodeId !== undefined && typeof value.homeNodeId !== "string") ||
        (value.lastConnectedAt !== undefined && typeof value.lastConnectedAt !== "string") ||
        (value.relayUrl !== undefined && typeof value.relayUrl !== "string") ||
        (value.wanOrigin !== undefined && typeof value.wanOrigin !== "string"))
        return undefined;
    return {
        directAddresses: [...value.directAddresses],
        ...(value.homeNodeId === undefined ? {} : { homeNodeId: value.homeNodeId }),
        ...(value.lastConnectedAt === undefined ? {} : { lastConnectedAt: value.lastConnectedAt }),
        ...(value.relayUrl === undefined ? {} : { relayUrl: value.relayUrl }),
        ...(value.wanOrigin === undefined ? {} : { wanOrigin: value.wanOrigin }),
    };
}
function projectDiagnosticEncryption(value) {
    if (!isRecord(value) || !hasExactOwnKeys(value, ["kdf", "key"]) || !isDiagnosticCryptoKey(value.key)) {
        return undefined;
    }
    const kdf = projectDiagnosticKdf(value.kdf);
    if (kdf === undefined)
        return undefined;
    return { kdf, key: value.key };
}
function projectDiagnosticKdf(value) {
    if (!isRecord(value) || !hasExactOwnKeys(value, ["hash", "iterations", "name", "saltBase64"])) {
        return undefined;
    }
    if (!(value.hash === "SHA-256" &&
        value.iterations === DIAGNOSTIC_ITERATIONS &&
        value.name === "PBKDF2" &&
        typeof value.saltBase64 === "string"))
        return undefined;
    return {
        hash: "SHA-256",
        iterations: DIAGNOSTIC_ITERATIONS,
        name: "PBKDF2",
        saltBase64: value.saltBase64,
    };
}
function isDiagnosticCryptoKey(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const key = value;
    const algorithm = key.algorithm;
    return key.extractable === false &&
        key.type === "secret" &&
        Array.isArray(key.usages) &&
        key.usages.length === 1 &&
        key.usages[0] === "encrypt" &&
        algorithm?.name === "AES-GCM";
}
//# sourceMappingURL=storage-worker.js.map